import React, { useEffect, useState } from "react";
import { ExternalLink, CheckCircle2, BarChart3 } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { getHistory } from "../../lib/api";
import { toast } from "sonner";

const RANGES = [
  ["1h", "Last 1h"],
  ["24h", "Last 24h"],
  ["7d", "Last 7 days"],
];

const CHARTS = [
  { key: "voltage", label: "Voltage", unit: "V", color: "#00FF66" },
  { key: "current", label: "Current", unit: "A", color: "#FFCC00" },
  { key: "power", label: "Power", unit: "W", color: "#3B9DFF" },
  { key: "pf", label: "Power Factor", unit: "", color: "#FF6BD6" },
  { key: "frequency", label: "Frequency", unit: "Hz", color: "#9D7BFF" },
  { key: "kwh", label: "Energy", unit: "kWh", color: "#FF8A3D" },
];

function LinkCard({ title, desc, children }) {
  return (
    <Panel className="p-5">
      <Overline>{title}</Overline>
      <p className="text-sm text-[var(--t2)] mt-1 mb-3">{desc}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </Panel>
  );
}

function LinkBtn({ children, testid, onClick, href }) {
  const cls = "inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-mono font-medium bg-[var(--surface2)] border border-[var(--bd)] hover:border-[var(--bd-strong)] transition-colors";
  if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className={cls}>{children}<ExternalLink size={12} /></a>;
  return <button onClick={onClick} data-testid={testid} className={cls}>{children}</button>;
}

export default function Grafana() {
  const { settings } = useApp();
  const [range, setRange] = useState("1h");
  const [data, setData] = useState([]);

  useEffect(() => {
    let on = true;
    getHistory(range).then((p) => on && setData(p)).catch(() => {});
    return () => { on = false; };
  }, [range]);

  return (
    <div className="space-y-6">
      <div>
        <Overline>Cloud Platforms</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Data Integrations</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <LinkCard title="Grafana Cloud" desc="Time-series dashboards.">
          <LinkBtn href={`${settings.grafanaUrl}?from=now-1h`} testid="grafana-1h">Last 1h</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?from=now-24h`} testid="grafana-24h">Last 24h</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?from=now-7d`} testid="grafana-7d">Last 7 days</LinkBtn>
        </LinkCard>
        <LinkCard title="ThingSpeak" desc="IoT analytics channels.">
          <LinkBtn href="https://thingspeak.com/channels" testid="thingspeak-a">Channel A</LinkBtn>
          <LinkBtn href="https://thingspeak.com/channels" testid="thingspeak-b">Channel B</LinkBtn>
          <LinkBtn testid="thingspeak-verify-a" onClick={() => toast.success("Channel A verified — last entry 2s ago")}><CheckCircle2 size={12} /> Verify A</LinkBtn>
          <LinkBtn testid="thingspeak-verify-b" onClick={() => toast.success("Channel B verified — last entry 2s ago")}><CheckCircle2 size={12} /> Verify B</LinkBtn>
        </LinkCard>
        <LinkCard title="Sheets & Tableau" desc="Exports & BI reports.">
          <LinkBtn href="https://sheets.google.com" testid="google-sheets">Google Sheets</LinkBtn>
          <LinkBtn href="https://public.tableau.com" testid="tableau">Tableau</LinkBtn>
        </LinkCard>
      </div>

      <Panel className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-[var(--accent)]" />
            <Overline>Historical Charts</Overline>
          </div>
          <div className="flex gap-1">
            {RANGES.map(([val, label]) => (
              <button
                key={val}
                onClick={() => setRange(val)}
                data-testid={`history-range-${val}`}
                className={`px-3 py-1.5 rounded-md text-xs font-mono transition-colors ${range === val ? "bg-[var(--accent)] text-black" : "bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)] hover:text-[var(--t1)]"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {CHARTS.map((c) => (
            <div key={c.key} className="bg-[var(--surface2)] border border-[var(--bd)] rounded-md p-4" data-testid={`chart-${c.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono font-medium">{c.label}</span>
                <span className="text-[10px] font-mono text-[var(--t2)]">{c.unit}</span>
              </div>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={data} margin={{ top: 5, right: 6, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
                  <XAxis dataKey="time" tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "var(--t2)" }} stroke="var(--bd)" />
                  <YAxis tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "var(--t2)" }} stroke="var(--bd)" domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--bd-strong)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 11 }} />
                  <Line type="monotone" dataKey={c.key} stroke={c.color} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
